<template>
    <div class="debug-index">
        <div class="header">
            <h1 class="text-h3 text-primary">
                🔧 Debug & Component Playground
            </h1>
            <p class="text-body-1 text-medium-emphasis">
                Interactive demos and testing tools for Flutor components
            </p>
        </div>

        <!-- Question Views Section -->
        <div class="section">
            <h2 class="text-h4 text-secondary section-title">
                📝 Question Views
            </h2>
            <p class="text-body-2 text-medium-emphasis section-subtitle">
                Full Skuilder question types with grading and answer submission
            </p>
            <div class="debug-grid">
                <v-card
                    v-for="page in questionPages"
                    :key="page.path"
                    class="debug-card"
                    :to="page.path"
                    hover
                >
                    <v-card-title class="card-title">
                        <span class="icon">{{ page.icon }}</span>
                        <span class="title-text">{{ page.title }}</span>
                    </v-card-title>
                    <v-card-text>
                        <p class="description">{{ page.description }}</p>
                        <div class="component-tag">
                            <code>{{ page.component }}</code>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Interactive Components Section -->
        <div class="section">
            <h2 class="text-h4 text-secondary section-title">
                🎤 Interactive Components
            </h2>
            <p class="text-body-2 text-medium-emphasis section-subtitle">
                Components that use microphone input and real-time interaction
            </p>
            <div class="debug-grid">
                <v-card
                    v-for="page in interactivePages"
                    :key="page.path"
                    class="debug-card"
                    :to="page.path"
                    hover
                >
                    <v-card-title class="card-title">
                        <span class="icon">{{ page.icon }}</span>
                        <span class="title-text">{{ page.title }}</span>
                    </v-card-title>
                    <v-card-text>
                        <p class="description">{{ page.description }}</p>
                        <div class="component-tag">
                            <code>{{ page.component }}</code>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Display Components Section -->
        <div class="section">
            <h2 class="text-h4 text-secondary section-title">
                🎼 Display Components
            </h2>
            <p class="text-body-2 text-medium-emphasis section-subtitle">
                Visual components for displaying music notation and fingering
                diagrams
            </p>
            <div class="debug-grid">
                <v-card
                    v-for="page in displayPages"
                    :key="page.path"
                    class="debug-card"
                    :to="page.path"
                    hover
                >
                    <v-card-title class="card-title">
                        <span class="icon">{{ page.icon }}</span>
                        <span class="title-text">{{ page.title }}</span>
                    </v-card-title>
                    <v-card-text>
                        <p class="description">{{ page.description }}</p>
                        <div class="component-tag">
                            <code>{{ page.component }}</code>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <v-card class="info-section">
            <v-card-title class="text-h5">About These Tools</v-card-title>
            <v-card-text>
                <p class="text-body-1">
                    These debug pages provide interactive playgrounds for
                    testing and demonstrating Flutor's components. Each page
                    includes:
                </p>
                <ul class="features-list">
                    <li>Live component preview with real-time updates</li>
                    <li>Interactive controls to adjust all props</li>
                    <li>Code generation with copy-to-clipboard</li>
                    <li>Example presets and common use cases</li>
                </ul>
                <p class="text-body-2 text-medium-emphasis">
                    These tools are for development and testing only and should
                    not be exposed in production builds.
                </p>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
interface DebugPage {
    title: string;
    description: string;
    path: string;
    icon: string;
    component: string;
}

const questionPages: DebugPage[] = [
    {
        title: "Sequenced Note Reading Question",
        description:
            "Self-grading question that requires playing a sequence of notes with accuracy tracking and performance scoring",
        path: "/dbg/sequenced-note-reading-question",
        icon: "🎼",
        component: "<SequencedNoteReadingQuestion />",
    },
    {
        title: "Sustained Note Question",
        description:
            "Self-grading question that requires holding a note for a sustained duration with real-time progress tracking",
        path: "/dbg/sustained-note",
        icon: "⏱️",
        component: "<SustainedNoteQuestion />",
    },
];

const interactivePages: DebugPage[] = [
    {
        title: "Sequenced Note Player",
        description:
            "Play a sequence of notes with visual feedback - tests the full SequencedNotePlayer component with configurable settings",
        path: "/dbg/sequenced-note-player",
        icon: "🎼",
        component: "<SequencedNotePlayer />",
    },
    {
        title: "Sequence Pitch Matcher",
        description:
            "Test sequence matching logic in isolation - configure a note sequence and play to see progression tracking",
        path: "/dbg/sequence-matcher",
        icon: "🎯",
        component: "SequencePitchMatcher",
    },
    {
        title: "Live Fingering Detection",
        description:
            "Real-time fingering chart that updates as you play notes, showing the detected note's fingering",
        path: "/dbg/live-fingering",
        icon: "🎤",
        component: "Pitch Detection + Fingering",
    },
    {
        title: "Listening Note",
        description:
            "Interactive note practice with real-time pitch detection and visual feedback",
        path: "/dbg/listening-note",
        icon: "🎯",
        component: "<ListeningNote />",
    },
    {
        title: "Pitch Detection",
        description:
            "Real-time pitch detection from microphone input with frequency and note analysis",
        path: "/dbg/pitch",
        icon: "🎤",
        component: "PitchDetection API",
    },
];

const displayPages: DebugPage[] = [
    {
        title: "Flute Fingering",
        description:
            "Interactive flute fingering diagram with controls for all keys and trill options",
        path: "/dbg/flute",
        icon: "🎺",
        component: "<FluteFingering />",
    },
    {
        title: "Music Note",
        description:
            "Single music note display on a staff with VexFlow, supporting all clefs and durations",
        path: "/dbg/note",
        icon: "🎵",
        component: "<MusicNote />",
    },
    {
        title: "Note with Fingering",
        description:
            "Combined display of a music note on staff with its corresponding fingering diagram",
        path: "/dbg/note-fingering",
        icon: "🎼",
        component: "<NoteWithFingering />",
    },
    {
        title: "Music Phrase",
        description:
            "Multiple notes in sequence with optional vertical fingering diagrams aligned to each note",
        path: "/dbg/phrase",
        icon: "📜",
        component: "<MusicPhrase />",
    },
    {
        title: "Pitch Offset Visualizer",
        description:
            "Visual indicator showing target pitch vs detected pitch with color-coded tuning feedback",
        path: "/dbg/pitch-offset-visualizer",
        icon: "🎚️",
        component: "<PitchOffsetVisualizer />",
    },
];
</script>

<style scoped>
.debug-index {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    margin-bottom: 40px;
    text-align: center;
}

.header h1 {
    margin-bottom: 12px;
}

.section {
    margin-bottom: 50px;
}

.section-title {
    margin-bottom: 8px;
}

.section-subtitle {
    margin-bottom: 24px;
}

.debug-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
    gap: 24px;
}

.debug-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.debug-card:hover {
    transform: scale(1.01);
}

.card-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 1.2rem;
    font-weight: 600;
    padding-bottom: 8px;
}

.icon {
    font-size: 2rem;
}

.title-text {
    flex: 1;
}

.description {
    margin-bottom: 16px;
    line-height: 1.6;
    min-height: 60px;
}

.component-tag {
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 0.85rem;
}

.info-section {
    margin-top: 40px;
}

.features-list {
    margin: 16px 0;
    padding-left: 24px;
    line-height: 1.8;
}

.features-list li {
    margin-bottom: 8px;
}

@media (max-width: 768px) {
    .debug-grid {
        grid-template-columns: 1fr;
    }
}
</style>
